<script setup>
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  modelValue: {
    type: [Object, Array],
    default: () => ({}),
  },
  boardId: {
    type: [Number, String],
    default: null,
  },
  dropdownMaxHeight: {
    type: String,
    default: 'max-h-80',
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();
const store = useStore();

// Products are board-scoped; load them for the funnel selected in the rule.
const products = useMapGetter('kanban/getProducts');

watch(
  () => props.boardId,
  boardId => {
    if (boardId) {
      store.dispatch('kanban/fetchProducts', { boardId: Number(boardId) });
    }
  },
  { immediate: true }
);

const normalizedParams = computed(() => {
  const value = props.modelValue;
  if (Array.isArray(value)) {
    const first = value[0];
    return typeof first === 'object' && first !== null ? first : {};
  }
  return typeof value === 'object' && value !== null ? value : {};
});

const productOptions = computed(() =>
  (products.value || []).map(product => ({
    id: product.id,
    name: product.name,
  }))
);

const selectedProduct = computed(() =>
  (products.value || []).find(
    product => product.id === normalizedParams.value.product_id
  )
);

const productSelectModel = computed({
  get: () =>
    selectedProduct.value
      ? { id: selectedProduct.value.id, name: selectedProduct.value.name }
      : null,
  set: option => {
    emit('update:modelValue', [
      { ...normalizedParams.value, product_id: option?.id ?? null },
    ]);
  },
});

const quantityModel = computed({
  get: () => normalizedParams.value.quantity ?? 1,
  set: value => {
    const num = Number(value) || 1;
    emit('update:modelValue', [
      { ...normalizedParams.value, quantity: num > 0 ? num : 1 },
    ]);
  },
});
</script>

<template>
  <div class="flex flex-wrap items-center gap-2">
    <div
      v-if="!boardId"
      class="flex items-center px-3 py-2 bg-n-amber-3 dark:bg-n-amber-3 border border-solid border-n-amber-6 dark:border-n-amber-6 rounded"
    >
      <span class="text-n-slate-11 text-sm">
        {{ t('AUTOMATION.KANBAN.SELECT_BOARD_FIRST') }}
      </span>
    </div>
    <template v-else>
      <SingleSelect
        v-model="productSelectModel"
        :options="productOptions"
        :placeholder="t('AUTOMATION.ACTION.PRODUCT_SELECT')"
        :dropdown-max-height="dropdownMaxHeight"
        disable-deselect
      />
      <NextInput
        v-model="quantityModel"
        type="number"
        size="sm"
        min="1"
        class="w-24"
        :placeholder="t('AUTOMATION.ACTION.PRODUCT_QUANTITY')"
      />
    </template>
  </div>
</template>
